import React, { useCallback, useState, type ReactNode } from 'react'
import { Pencil, X } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { Wizard } from '@klear/ui/wizard/wizard'
import { useWizardDirtyLeaveGuard } from '@klear/ui/wizard/dirty-leave-guard'

export type EntityWizardMode = 'create' | 'edit' | 'view'

export type EntityWizardShellProps<Mode extends EntityWizardMode = EntityWizardMode> = {
  mode: Mode
  entityLabel: string
  entityName?: string
  title?: string
  canEdit?: boolean
  viewActions?: ReactNode
  isLoading?: boolean
  error?: { message: string } | null
  notFound?: boolean
  notFoundLabel?: string
  onBack: () => void
  onCancelEdit?: () => void
  onEdit?: () => void
  isBusy?: boolean
  children: (args: {
    activeMode: Mode | 'edit'
    onNavStateChange: (nav: WizardStepGroupNavState) => void
    cancelEdit: () => void
    requestExit: () => void
    onDirtyChange: (dirty: boolean) => void
  }) => ReactNode
  footer?: ReactNode
}

function resolveEntityWizardTitle(
  mode: EntityWizardMode,
  entityLabel: string,
  entityName: string | undefined,
  title: string | undefined
): string {
  if (title != null) return title
  if (mode === 'create') return `Create ${entityLabel}`
  return entityName ?? entityLabel
}

export function EntityWizardShell<Mode extends EntityWizardMode = EntityWizardMode>({
  mode,
  entityLabel,
  entityName,
  title,
  canEdit = false,
  viewActions,
  isLoading = false,
  error,
  notFound = false,
  notFoundLabel = 'Not found',
  onBack,
  onCancelEdit,
  onEdit,
  isBusy = false,
  children,
  footer
}: EntityWizardShellProps<Mode>) {
  const [editingFromView, setEditingFromView] = useState(false)
  const activeMode = (mode === 'view' && editingFromView ? 'edit' : mode) as Mode | 'edit'
  const [nav, setNav] = useState<WizardStepGroupNavState | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const resolvedTitle = resolveEntityWizardTitle(mode, entityLabel, entityName, title)
  const isEditing = activeMode !== 'view'
  const { LeaveConfirmDialog, guardExit } = useWizardDirtyLeaveGuard({ when: isEditing && isDirty })

  const cancelEditRaw = useCallback(() => {
    if (mode === 'edit') {
      ;(onCancelEdit ?? onBack)()
      return
    }
    setIsDirty(false)
    setEditingFromView(false)
  }, [mode, onCancelEdit, onBack])

  const requestExit = useCallback(() => {
    guardExit(onBack)()
  }, [guardExit, onBack])

  const cancelEdit = useCallback(() => {
    guardExit(cancelEditRaw)()
  }, [guardExit, cancelEditRaw])

  if (mode !== 'create' && isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading...</div>
  }
  if (error) {
    return <div className="container mx-auto px-4 py-8 text-center text-red-600">Error: {error.message}</div>
  }
  if (mode !== 'create' && notFound) {
    return <div className="container mx-auto px-4 py-8 text-center">{notFoundLabel}</div>
  }

  function startEdit() {
    if (onEdit) {
      onEdit()
      return
    }
    setEditingFromView(true)
  }

  const onCancelHeader = activeMode === 'create' ? requestExit : cancelEdit

  let headerActions: ReactNode
  if (isEditing) {
    headerActions = (
      <Button type="button" variant="outline" onClick={onCancelHeader} disabled={isBusy}>
        <X className="h-4 w-4" />
        Cancel
      </Button>
    )
  } else if (canEdit || viewActions) {
    headerActions = (
      <div className="flex gap-2">
        {viewActions}
        {canEdit ? (
          <Button type="button" onClick={startEdit} disabled={isBusy}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <>
      {LeaveConfirmDialog}
      <form className="contents" onSubmit={(e) => e.preventDefault()}>
        <Wizard
          name={resolvedTitle}
          steps={nav?.steps ?? []}
          activeStepId={nav?.activeStepId ?? ''}
          onStepSelect={nav?.onStepSelect}
          isStepDisabled={nav?.isStepDisabled}
          onBack={requestExit}
          headerActions={headerActions}
        >
          {children({
            activeMode,
            onNavStateChange: setNav,
            cancelEdit,
            requestExit,
            onDirtyChange: setIsDirty
          })}
        </Wizard>
      </form>
      {footer}
    </>
  )
}
